import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  List,
  Tag,
  Rate,
  Typography,
  Statistic,
  Button,
  Tabs,
  Empty,
  message,
} from 'antd';
import {
  StarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  LikeOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const Reviews = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  // Fetch reviews
  const { data: reviewsData, isLoading } = useQuery(
    ['provider-reviews', filter],
    async () => {
      try {
        const params = filter !== 'all' ? `?filter=${filter}` : '';
        const response = await api.get(`/provider/reviews${params}`);
        return response.data;
      } catch (error) {
        console.error('Reviews API error:', error);
        throw error;
      }
    },
    {
      enabled: !!localStorage.getItem('token') && !!localStorage.getItem('user'),
    },
  );

  const reviews = reviewsData?.data?.reviews || reviewsData?.reviews || [];
  const stats = reviewsData?.stats || {};
  const avgRating = stats?.average_rating || 0;
  const totalReviews = stats?.total_reviews || 0;

  const getRatingColor = (rating) => {
    if (rating >= 4) return '#10b981';
    if (rating >= 3) return '#faad14';
    return '#ff4d4f';
  };

  // Filter reviews locally based on the selected tab
  const filteredReviews =
    filter === 'all'
      ? reviews
      : filter === 'positive'
        ? reviews.filter((r) => r.rating >= 4)
        : filter === 'neutral'
          ? reviews.filter((r) => r.rating === 3)
          : reviews.filter((r) => r.rating <= 2);

  const renderReviewItem = (review) => (
    <List.Item
      key={review.id}
      actions={[
        <Button
          type="link"
          size="small"
          icon={<LikeOutlined />}
          onClick={() => message.info('Thank you for your response!')}
        >
          Helpful
        </Button>,
      ]}
    >
      <List.Item.Meta
        avatar={
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <UserOutlined />
          </div>
        }
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>{review.customer_name || 'Anonymous'}</Text>
            <Rate disabled value={review.rating} style={{ fontSize: 14 }} />
          </div>
        }
        description={
          <div>
            <div style={{ marginBottom: 8 }}>
              <Text
                strong
                style={{ cursor: 'pointer', color: '#1890ff' }}
                onClick={() => navigate(`/product/${review.product_id}`)}
              >
                {review.product_name || 'View Product'}
              </Text>
            </div>
            {review.comment && <div style={{ marginBottom: 8 }}>{review.comment}</div>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {review.items?.map((item, idx) => (
                <Tag key={idx} color="blue">
                  {item}
                </Tag>
              ))}
            </div>
            <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              {dayjs(review.created_at).format('MMM D, YYYY HH:mm')}
              {review.order_id && <span> • Order #{review.order_id}</span>}
            </div>
          </div>
        }
      />
    </List.Item>
  );

  const renderRatingBreakdown = () => (
    <Card title="Rating Breakdown">
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={8}>
          <div style={{ textAlign: 'center' }}>
            <Statistic
              value={avgRating}
              suffix="/ 5"
              valueStyle={{
                fontSize: 48,
                color: getRatingColor(avgRating),
              }}
            />
            <Rate disabled value={avgRating} allowHalf />
            <div style={{ marginTop: 8, color: '#888' }}>Based on {totalReviews} reviews</div>
          </div>
        </Col>
        <Col xs={24} sm={16}>
          {[5, 4, 3, 2, 1].map((star) => {
            const count = stats?.rating_breakdown?.[star] || 0;
            const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
            return (
              <Row key={star} gutter={8} style={{ marginBottom: 8 }}>
                <Col xs={3}>
                  <Text>
                    {star} <StarOutlined style={{ color: '#faad14' }} />
                  </Text>
                </Col>
                <Col xs={14}>
                  <div
                    style={{
                      height: 8,
                      background: '#f0f0f0',
                      borderRadius: 4,
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${percentage}%`,
                        background: '#faad14',
                        borderRadius: 4,
                      }}
                    />
                  </div>
                </Col>
                <Col xs={4}>
                  <Text type="secondary">{count}</Text>
                </Col>
              </Row>
            );
          })}
        </Col>
      </Row>
    </Card>
  );

  const renderRecentReviews = () => (
    <Card>
      <List
        loading={isLoading}
        dataSource={filteredReviews}
        renderItem={renderReviewItem}
        locale={{ emptyText: <Empty description="No reviews yet" /> }}
      />
    </Card>
  );

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <StarOutlined style={{ marginRight: '8px' }} />
        Reviews
      </Title>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Average Rating"
              value={avgRating}
              precision={1}
              suffix="/ 5"
              valueStyle={{ color: getRatingColor(avgRating) }}
              prefix={<StarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Total Reviews" value={totalReviews} prefix={<UserOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="5-Star Reviews"
              value={stats?.rating_breakdown?.[5] || 0}
              valueStyle={{ color: '#10b981' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Response Rate"
              value={stats?.response_rate || 100}
              suffix="%"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="all"
        onChange={setFilter}
        items={[
          {
            key: 'all',
            label: 'All Reviews',
            children: renderRecentReviews(),
          },
          {
            key: 'positive',
            label: 'Positive (4-5★)',
            children: renderRecentReviews(),
          },
          {
            key: 'neutral',
            label: 'Neutral (3★)',
            children: renderRecentReviews(),
          },
          {
            key: 'negative',
            label: 'Negative (1-2★)',
            children: renderRecentReviews(),
          },
        ]}
      />

      {renderRatingBreakdown()}
    </div>
  );
};

export default Reviews;
